/**
 * Service for fetching warehouse and region data
 */

import { Region, Warehouse } from '../types/warehouse';
import { getActiveFCsByDataCenter } from './configurationService';
import { FCInfo } from '../types/configuration';

// Both WarehouseSelector and WarehouseUrlHandler call getAllRegions() on
// mount, and they mount at the same time on initial page load. Without this
// cache, that fires two identical requests for /data/warehouses.json (and,
// if that falls through, two identical GetActiveFCsByDataCenter calls too).
// Caching the in-flight promise means every caller within the same page
// load shares a single request; a later caller after it resolves reuses the
// resolved result instead of re-fetching.
let regionsRequest: Promise<Region[]> | null = null;

/**
 * Get all regions with their warehouses.
 *
 * Primary: Fetches pre-generated static warehouse data from /data/warehouses.json
 * (uploaded to S3 by a scheduled Lambda, no API Gateway call needed).
 * Fallback: Calls GetActiveFCsByDataCenter API if static file is unavailable.
 * Last resort: Returns hardcoded mock data.
 *
 * Deduplicated: concurrent/repeated calls share one underlying request
 * instead of each firing their own fetch (see regionsRequest above).
 */
export function getAllRegions(): Promise<Region[]> {
    if (!regionsRequest) {
        regionsRequest = fetchAllRegions().catch((error) => {
            // Don't cache a failed attempt - let the next call retry from scratch.
            regionsRequest = null;
            throw error;
        });
    }
    return regionsRequest;
}

/**
 * Test-only: clears the cached getAllRegions() request so each test starts
 * from a clean slate instead of reusing a previous test's cached promise.
 */
export function __resetRegionsCacheForTests(): void {
    regionsRequest = null;
}

async function fetchAllRegions(): Promise<Region[]> {
    // Primary: fetch pre-generated static file (fast, no API call)
    try {
        const response = await fetch('/data/warehouses.json');
        if (response.ok) {
            const data = await response.json();
            if (data.regions && data.regions.length > 0) {
                return data.regions as Region[];
            }
        }
    } catch (error) {
        console.warn('Static warehouse data unavailable:', error);
    }

    // Fallback: live API call
    try {
        const fcsByDataCenter = await getActiveFCsByDataCenter(['IAD', 'DUB', 'NRT']);
        const regions = convertFCsToRegions(fcsByDataCenter);
        if (regions.length > 0) {
            return regions;
        }
    } catch (error) {
        console.warn('API fallback also failed:', error);
    }

    // Last resort: hardcoded mock data
    return getMockRegions();
}

/**
 * Convert FCConfigurationService data center grouping to Region[] format.
 * Uses the data center name directly as the region code (matches legacy behavior).
 */
function convertFCsToRegions(fcsByDataCenter: Record<string, FCInfo[]>): Region[] {
    const regions: Region[] = [];

    for (const [dataCenter, fcs] of Object.entries(fcsByDataCenter)) {
        const regionCode = dataCenter.toUpperCase();

        const warehouses: Warehouse[] = fcs.map(fc => ({
            warehouseId: fc.warehouseId,
            regionCode,
            displayName: fc.warehouseId,
        }));

        // Merge into existing region if one already exists for this regionCode
        const existing = regions.find(r => r.regionCode === regionCode);
        if (existing) {
            existing.warehouses.push(...warehouses);
        } else {
            regions.push({ regionCode, displayName: regionCode, warehouses });
        }
    }

    // Sort regions alphabetically, then warehouses within each region
    regions.sort((a, b) => a.regionCode.localeCompare(b.regionCode));
    for (const region of regions) {
        region.warehouses.sort((a, b) => a.warehouseId.localeCompare(b.warehouseId));
    }

    return regions;
}

/**
 * Get the currently selected warehouse from local storage
 */
export function getCurrentWarehouse(): Warehouse {
    console.log('Getting current warehouse from localStorage...');
    const stored = localStorage.getItem('selectedWarehouse');
    console.log('Raw stored value:', stored);

    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            console.log('Successfully parsed warehouse:', parsed);
            return parsed;
        } catch (e) {
            console.error('Failed to parse stored warehouse:', e);
        }
    }

    // Default to DCA2 in IAD region
    const defaultWarehouse: Warehouse = {
        warehouseId: 'DCA2',
        regionCode: 'IAD',
        displayName: 'DCA2'
    };
    console.log('Using default warehouse:', defaultWarehouse);
    return defaultWarehouse;
}

/**
 * Set the currently selected warehouse in local storage
 */
export function setCurrentWarehouse(warehouse: Warehouse): void {
    console.log('Saving warehouse to localStorage:', warehouse);
    const serialized = JSON.stringify(warehouse);
    console.log('Serialized warehouse:', serialized);
    localStorage.setItem('selectedWarehouse', serialized);
    console.log('Warehouse saved to localStorage successfully');
    // Note: the `warehouseChanged` event (which busts the apiClient cache) is
    // dispatched by the callers that actually change the warehouse —
    // WarehouseSelector.handleSelect and WarehouseUrlHandler — so it fires for
    // both selector- and URL-driven changes.
}

/**
 * Mock data for regions and warehouses
 * Simulates Amazon fulfillment centers across different regions
 */
function getMockRegions(): Region[] {
    return [
        {
            regionCode: 'IAD',
            displayName: 'North America (IAD)',
            warehouses: [
                { warehouseId: 'IND1', regionCode: 'IAD', displayName: 'IND1' },
                { warehouseId: 'BFI1', regionCode: 'IAD', displayName: 'BFI1' },
                { warehouseId: 'DCA2', regionCode: 'IAD', displayName: 'DCA2' },
                { warehouseId: 'DCA6', regionCode: 'IAD', displayName: 'DCA6' },
                { warehouseId: 'BWI2', regionCode: 'IAD', displayName: 'BWI2' },
                { warehouseId: 'PHL1', regionCode: 'IAD', displayName: 'PHL1' },
                { warehouseId: 'PHL5', regionCode: 'IAD', displayName: 'PHL5' },
                { warehouseId: 'EWR4', regionCode: 'IAD', displayName: 'EWR4' },
                { warehouseId: 'EWR9', regionCode: 'IAD', displayName: 'EWR9' }
            ]
        },
        {
            regionCode: 'DUB',
            displayName: 'Europe (DUB)',
            warehouses: [
                { warehouseId: 'LBA1', regionCode: 'DUB', displayName: 'LBA1' },
                { warehouseId: 'LBA2', regionCode: 'DUB', displayName: 'LBA2' },
                { warehouseId: 'MAN1', regionCode: 'DUB', displayName: 'MAN1' },
                { warehouseId: 'MAN3', regionCode: 'DUB', displayName: 'MAN3' },
                { warehouseId: 'BER1', regionCode: 'DUB', displayName: 'BER1' },
                { warehouseId: 'DUS2', regionCode: 'DUB', displayName: 'DUS2' }
            ]
        },
        {
            regionCode: 'NRT',
            displayName: 'Asia Pacific (NRT)',
            warehouses: [
                { warehouseId: 'NRT1', regionCode: 'NRT', displayName: 'NRT1' },
                { warehouseId: 'NRT2', regionCode: 'NRT', displayName: 'NRT2' },
                { warehouseId: 'HND1', regionCode: 'NRT', displayName: 'HND1' },
                { warehouseId: 'OSA1', regionCode: 'NRT', displayName: 'OSA1' },
                { warehouseId: 'SIN2', regionCode: 'NRT', displayName: 'SIN2' },
                { warehouseId: 'SYD1', regionCode: 'NRT', displayName: 'SYD1' }
            ]
        },
        {
            regionCode: 'ZAZ',
            displayName: 'South America (ZAZ)',
            warehouses: [
                { warehouseId: 'GRU1', regionCode: 'ZAZ', displayName: 'GRU1' },
                { warehouseId: 'GRU2', regionCode: 'ZAZ', displayName: 'GRU2' },
                { warehouseId: 'SAO1', regionCode: 'ZAZ', displayName: 'SAO1' },
                { warehouseId: 'MEX1', regionCode: 'ZAZ', displayName: 'MEX1' }
            ]
        }
    ];
}
